import { type Upload } from '@shared/all/domains/uploads';
import { resolveUploadContentType } from '@shared/all/uploadTypes';
import { useRef, useState } from 'react';

import { composeUpload } from '../helpers/composeUpload';
import type { FactiiiContext } from '../helpers/composeUpload.types';
import toast from '../helpers/toast';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import {
  pickFile as platformPickFile,
  pickImage as platformPickImage,
  type PickImageOptions,
  pickMedia as platformPickMedia,
  type PickMediaOptions,
  type PickResult,
  uploadToStorage
} from '../helpers/uploadHelper';

export type UploadItem = {
  key: string;
  uri: string;
  fileName?: string;
  width?: number;
  height?: number;
  isVideo?: boolean;
  // Local file metadata for pre-upload validation (cross-post preview)
  sizeBytes?: number;
  mimeType?: string;
  durationSec?: number | null;
  factiii?: FactiiiContext | null;
};

type UploadOneOptions = {
  factiii?: FactiiiContext | null;
  /**
   * When provided, this upload reports 0–1 progress under `uploadProgress[key]`
   * and can be cancelled with `cancelUpload(key)`. Use the file's map key so the
   * UI can map progress back to its thumbnail.
   */
  key?: string;
  /**
   * Chat attachments pass their conversation so the server can decide the
   * upload's visibility from the channel's space — a PRIVATE space's files must
   * not land in the public bucket. Never send an isPrivate flag from here; the
   * server owns that.
   */
  conversationId?: string;
  /**
   * Set for a file that will be attached to a post. It lands in the private
   * bucket, and the post request publishes it if the post is public. Leave it
   * off for avatars, banners, product images and factiii photos: nothing moves
   * those, so they have to land public.
   */
  forPost?: boolean;
};

const useUpload = (client: WrappedTRPCClient) => {
  const [files, setFiles] = useState<Record<string, UploadItem>>({});
  const [uploadingKeys, setUploadingKeys] = useState<string[]>([]);
  // 0–1 upload progress per key, populated when uploadOne is given a `key`.
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );
  // One AbortController per in-flight keyed upload, so cancelUpload can stop it.
  const abortControllers = useRef<Record<string, AbortController>>({});

  const setProgress = (key: string, fraction: number): void => {
    setUploadProgress((prev) => ({ ...prev, [key]: Math.min(1, fraction) }));
  };

  const clearProgress = (key: string): void => {
    delete abortControllers.current[key];
    setUploadProgress((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const cancelUpload = (key: string): void => {
    abortControllers.current[key]?.abort();
    clearProgress(key);
  };

  const cancelAll = (): void => {
    for (const key of Object.keys(abortControllers.current)) cancelUpload(key);
  };

  const pickImage = async (
    options?: PickImageOptions
  ): Promise<PickResult | null> => {
    return platformPickImage(options);
  };

  const pickMedia = async (
    options?: PickMediaOptions
  ): Promise<PickResult | null> => {
    return platformPickMedia(options);
  };

  const pickFile = async (): Promise<PickResult | null> => {
    return platformPickFile();
  };

  // --- State management ---

  const addFile = (
    key: string,
    data: {
      uri: string;
      fileName?: string;
      width?: number;
      height?: number;
      isVideo?: boolean;
      sizeBytes?: number;
      mimeType?: string;
      durationSec?: number | null;
      factiii?: FactiiiContext | null;
    }
  ): UploadItem => {
    const item: UploadItem = {
      key,
      uri: data.uri,
      fileName: data.fileName,
      width: data.width,
      height: data.height,
      isVideo: data.isVideo,
      sizeBytes: data.sizeBytes,
      mimeType: data.mimeType,
      durationSec: data.durationSec,
      factiii: data.factiii
    };
    setFiles((prev) => ({ ...prev, [key]: item }));
    return item;
  };

  const addFiles = (items: UploadItem[]): void => {
    setFiles((prev) => {
      const next = { ...prev };
      for (const item of items) next[item.key] = item;
      return next;
    });
  };

  const removeFile = (key: string): void => {
    setFiles((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const clear = (): void => {
    setFiles({});
  };

  // --- Upload methods ---

  const uploadOne = async (
    uri: string,
    fileName?: string,
    dimensions?: { width: number; height: number },
    options?: UploadOneOptions
  ): Promise<Upload | null> => {
    const key = options?.key;
    const controller = key ? new AbortController() : undefined;
    if (key && controller) {
      abortControllers.current[key] = controller;
      setProgress(key, 0);
    }
    try {
      const factiii = options?.factiii ?? null;

      // Step 1: Compose locally — hashes raw, paints/encodes view.
      const source = uri.startsWith('blob:')
        ? {
            kind: 'blob' as const,
            blob: await (await fetch(uri)).blob(),
            fileName
          }
        : { kind: 'uri' as const, uri };
      const composed = await composeUpload(source, factiii);

      // Declare the type of the bytes compose just produced, not the type of
      // the name the picker reported — on iOS those differ. See
      // resolveUploadContentType for why.
      const contentType = resolveUploadContentType(
        composed.contentType,
        fileName,
        uri
      );

      // Step 2: Reserve raw + view keys + presigned PUTs from server.
      const [urlError, urls] = await client.uploads.getUploadUrls.mutate({
        contentType,
        name: fileName || 'file',
        isFactiii: !!factiii,
        ...(factiii && { factiiiId: factiii.factiiiId }),
        ...(options?.conversationId && {
          conversationId: options.conversationId
        }),
        ...(options?.forPost && { forPost: true }),
        rawSha256: composed.sha256,
        rawSize: composed.rawSize,
        // Sizes and hashes are signed into the PUT URLs, so they have to be
        // declared before the URLs exist. Compose already produced both.
        ...(contentType.startsWith('image/') && {
          viewSha256: composed.viewSha256,
          viewSize: composed.viewSize
        }),
        ...(dimensions && {
          width: dimensions.width,
          height: dimensions.height
        })
      });

      if (urlError || !urls) {
        toast(
          `Failed to start upload: ${urlError?.message || 'Unknown error'}`,
          '',
          'error'
        );
        return null;
      }

      // Step 3: Dual PUT in parallel — raw always, VIEW variant only for images.
      // Pass through native file URIs so the mobile uploader can attach them
      // directly (skips FileReader→base64 round-trip that corrupts binary
      // bytes and fails the signed checksum). Web ignores the URI field.
      const isImage = contentType.startsWith('image/');

      // Combine raw + view byte progress (weighted by size) into one 0–1
      // fraction per key. Each variant reports its own fraction; we scale by
      // its byte count so the bar reflects total bytes sent.
      const rawBytes = composed.rawBlob.size;
      const viewBytes = isImage && urls.view ? composed.viewBlob.size : 0;
      const totalBytes = rawBytes + viewBytes || 1;
      let rawSent = 0;
      let viewSent = 0;
      const progressOpts = (bytes: number, assign: (sent: number) => void) =>
        key && controller
          ? {
              signal: controller.signal,
              onProgress: (fraction: number) => {
                assign(fraction * bytes);
                setProgress(key, (rawSent + viewSent) / totalBytes);
              }
            }
          : undefined;

      const rawPut = uploadToStorage(
        { blob: composed.rawBlob, uri: composed.rawUri },
        urls.raw.url,
        urls.raw.headers,
        progressOpts(rawBytes, (sent) => (rawSent = sent))
      );
      const viewPut =
        isImage && urls.view
          ? uploadToStorage(
              { blob: composed.viewBlob, uri: composed.viewUri },
              urls.view.url,
              urls.view.headers,
              progressOpts(viewBytes, (sent) => (viewSent = sent))
            )
          : Promise.resolve(0);

      const [rawSize, viewSize] = await Promise.all([rawPut, viewPut]);

      // Step 4: Finalize — server validates magic bytes + sha256, inserts variants.
      // Server schema uses `view` for the VIEW variant payload (kind, not display).
      const [finalizeError, finalized] =
        await client.uploads.finalizeUpload.mutate({
          uploadId: urls.uploadId,
          isFactiii: !!factiii,
          raw: {
            key: urls.raw.key,
            size: rawSize,
            sha256: composed.sha256,
            ...(dimensions && {
              width: dimensions.width,
              height: dimensions.height
            })
          },
          view:
            isImage && urls.view
              ? {
                  key: urls.view.key,
                  size: viewSize,
                  sha256: composed.viewSha256,
                  width: composed.viewWidth,
                  height: composed.viewHeight
                }
              : null
        });

      if (finalizeError || !finalized) {
        toast(
          `Failed to finalize upload: ${finalizeError?.message || 'Unknown error'}`,
          '',
          'error'
        );
        return null;
      }

      return {
        id: finalized.id,
        type: finalized.type,
        name: finalized.name,
        isFactiii: !!factiii,
        display:
          isImage && urls.view
            ? {
                key: urls.view.key,
                width: composed.viewWidth,
                height: composed.viewHeight,
                contentType: 'image/jpeg'
              }
            : {
                key: urls.raw.key,
                width: null,
                height: null,
                contentType: composed.contentType
              }
      };
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        return null; // user cancelled — no error toast
      }
      console.error('Upload failed', e);
      // Surface the underlying reason (upload status + body, compose failure) —
      // without it a failed upload is a dead end for anyone triaging a report.
      toast(
        `Failed to upload ${fileName || 'file'}`,
        e instanceof Error ? e.message : '',
        'error'
      );
      return null;
    } finally {
      if (key) clearProgress(key);
    }
  };

  const upload = async (
    options?: Pick<UploadOneOptions, 'conversationId' | 'forPost'>
  ): Promise<Record<string, Upload>> => {
    const keys = Object.keys(files);
    setUploadingKeys(keys);
    try {
      const results: Record<string, Upload> = {};
      for (const [key, item] of Object.entries(files)) {
        const dimensions =
          item.width && item.height
            ? { width: item.width, height: item.height }
            : undefined;
        // Keyed so `uploadProgress[key]` tracks each file — the chat bubble
        // draws a bar from it while the bytes go up.
        const result = await uploadOne(item.uri, item.fileName, dimensions, {
          key,
          factiii: item.factiii,
          ...(options?.conversationId && {
            conversationId: options.conversationId
          }),
          ...(options?.forPost && { forPost: true })
        });
        if (result) {
          results[key] = result;
        } else {
          return {};
        }
      }
      return results;
    } finally {
      setUploadingKeys([]);
    }
  };

  return {
    files,
    uploadingKeys,
    uploadProgress,
    pickImage,
    pickMedia,
    pickFile,
    addFile,
    addFiles,
    removeFile,
    clear,
    uploadOne,
    upload,
    cancelUpload,
    cancelAll
  };
};

export default useUpload;
